import React, { useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import StatCard from '../components/StatCard';
import AlertCard from '../components/AlertCard';
import Modal from '../components/Modal';
import api from '../utils/api';
import { Users, Package, CurrencyDollar, TrendDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsHeight, setAlertsHeight] = useState('400px');
  const [showIngresosModal, setShowIngresosModal] = useState(false);
  const [ingresosDetalle, setIngresosDetalle] = useState([]);
  const [showEgresosModal, setShowEgresosModal] = useState(false);
  const [egresosDetalle, setEgresosDetalle] = useState([]);
  const [egresosTotal, setEgresosTotal] = useState({ total: 0, count: 0 });

  useEffect(() => {
    fetchDashboardData();
    
    // Calculate alerts section height based on viewport
    const calculateHeight = () => {
      const vh = window.innerHeight;
      const isLandscape = window.innerWidth > window.innerHeight;
      const isMobile = vh < 500 || window.innerWidth < 768;
      
      if (isMobile && isLandscape) {
        // Landscape mobile - smaller height
        setAlertsHeight(`${Math.max(vh - 180, 150)}px`);
      } else if (isMobile) {
        // Portrait mobile - medium height
        setAlertsHeight(`${Math.max(vh - 420, 250)}px`);
      } else {
        // Desktop - larger height to show more content
        setAlertsHeight(`${Math.max(vh - 350, 400)}px`);
      }
    };
    
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    window.addEventListener('orientationchange', calculateHeight);
    
    return () => {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('orientationchange', calculateHeight);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, alertsRes, proveedoresRes, dominiosRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/alerts'),
        api.get('/proveedores'),
        api.get('/dominios')
      ]);

      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      
      // Calcular egresos del mes (proveedores + dominios)
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      let egresosTotal = 0;
      let egresosCXP = 0; // CXP = total sin pagados
      let egresosCount = 0;
      
      // Proveedores del mes
      proveedoresRes.data.forEach(p => {
        if (p.fecha_vencimiento) {
          const parts = p.fecha_vencimiento.split('-');
          if (parts.length === 3) {
            const año = parseInt(parts[0], 10);
            const mes = parseInt(parts[1], 10);
            if (mes === currentMonth && año === currentYear && ['por vencer', 'vencido', 'activo', 'pagado'].includes(p.estado)) {
              egresosTotal += p.precio || 0;
              egresosCount++;
              if (p.estado !== 'pagado') {
                egresosCXP += p.precio || 0;
              }
            }
          }
        }
      });
      
      // Dominios del mes
      dominiosRes.data.forEach(d => {
        if (d.fecha_vencimiento) {
          const parts = d.fecha_vencimiento.split('-');
          if (parts.length === 3) {
            const año = parseInt(parts[0], 10);
            const mes = parseInt(parts[1], 10);
            if (mes === currentMonth && año === currentYear && ['por vencer', 'vencido', 'activo', 'pagado'].includes(d.estado)) {
              egresosTotal += d.anualidad || 0;
              egresosCount++;
              if (d.estado !== 'pagado') {
                egresosCXP += d.anualidad || 0;
              }
            }
          }
        }
      });
      
      setEgresosTotal({ total: egresosTotal, cxp: egresosCXP, count: egresosCount });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchIngresosDetalle = async () => {
    try {
      const [productosRes, dominiosRes, serviciosRes] = await Promise.all([
        api.get('/productos'),
        api.get('/dominios'),
        api.get('/servicios')
      ]);
      const productos = productosRes.data;
      const dominios = dominiosRes.data;
      const servicios = serviciosRes.data;
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      // Crear mapa de dominios por ID
      const dominiosMap = {};
      dominios.forEach(d => {
        dominiosMap[d.id] = d.nombre;
      });
      
      // Categorías de servicios
      const serviciosCategorias = ['Soporte', 'Seguridad', 'Mantenimiento', 'Desarrollo', 'Marketing'];
      
      // Filtrar productos (Hosting) del mes
      const productosMes = productos.filter(p => {
        // Excluir servicios de la lista de productos
        if (serviciosCategorias.includes(p.categoria)) return false;
        
        const fechaVenc = p.fecha_vencimiento;
        if (!fechaVenc) return false;
        
        try {
          const parts = fechaVenc.split('-');
          if (parts.length !== 3) return false;
          
          const añoProducto = parseInt(parts[0], 10);
          const mesProducto = parseInt(parts[1], 10);
          const estado = p.estado || '';
          
          return mesProducto === currentMonth && 
                 añoProducto === currentYear && 
                 ['pagado', 'por vencer', 'vencido', 'activo'].includes(estado);
        } catch {
          return false;
        }
      }).map(p => ({
        ...p,
        dominio_nombre: dominiosMap[p.dominio_id] || p.nombre,
        tipo: 'Hosting'
      }));
      
      // Filtrar servicios del mes (usan fecha_registro como vencimiento)
      const serviciosMes = servicios.filter(s => {
        const fechaVenc = s.fecha_vencimiento || s.fecha_registro;
        if (!fechaVenc) return false;
        
        try {
          const parts = fechaVenc.split('-');
          if (parts.length !== 3) return false;
          
          const añoServicio = parseInt(parts[0], 10);
          const mesServicio = parseInt(parts[1], 10);
          const estado = s.estado || '';
          
          return mesServicio === currentMonth && 
                 añoServicio === currentYear && 
                 ['pagado', 'por vencer', 'vencido', 'activo'].includes(estado);
        } catch {
          return false;
        }
      }).map(s => ({
        ...s,
        dominio_nombre: s.nombre,
        fecha_vencimiento: s.fecha_vencimiento || s.fecha_registro,
        tipo: 'Servicio'
      }));
      
      // Combinar productos y servicios
      const todosMes = [...productosMes, ...serviciosMes];
      
      setIngresosDetalle(todosMes);
      setShowIngresosModal(true);
    } catch (error) {
      console.error('Error fetching ingresos detalle:', error);
      toast.error('Error al cargar detalle de ingresos');
    }
  };

  const fetchEgresosDetalle = async () => {
    try {
      const [proveedoresRes, dominiosRes] = await Promise.all([
        api.get('/proveedores'),
        api.get('/dominios')
      ]);
      
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      const items = [];
      
      // Proveedores del mes
      proveedoresRes.data.forEach(p => {
        if (p.fecha_vencimiento) {
          const parts = p.fecha_vencimiento.split('-');
          if (parts.length === 3) {
            const año = parseInt(parts[0], 10);
            const mes = parseInt(parts[1], 10);
            if (mes === currentMonth && año === currentYear && ['por vencer', 'vencido', 'activo', 'pagado'].includes(p.estado)) {
              items.push({
                nombre: p.nombre,
                tipo: p.tipo || 'Egreso',
                fecha_vencimiento: p.fecha_vencimiento,
                precio: p.precio || 0,
                estado: p.estado
              });
            }
          }
        }
      });
      
      // Dominios del mes
      dominiosRes.data.forEach(d => {
        if (d.fecha_vencimiento) {
          const parts = d.fecha_vencimiento.split('-');
          if (parts.length === 3) {
            const año = parseInt(parts[0], 10);
            const mes = parseInt(parts[1], 10);
            if (mes === currentMonth && año === currentYear && ['por vencer', 'vencido', 'activo', 'pagado'].includes(d.estado)) {
              items.push({
                nombre: d.nombre,
                tipo: 'Dominio',
                fecha_vencimiento: d.fecha_vencimiento,
                precio: d.anualidad || 0,
                estado: d.estado
              });
            }
          }
        }
      });
      
      setEgresosDetalle(items);
      setShowEgresosModal(true);
    } catch (error) {
      console.error('Error fetching egresos detalle:', error);
      toast.error('Error al cargar detalle de egresos');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0d0f14]">
        <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen">
      <Topbar 
        title="Dashboard" 
        breadcrumb="Visionweb / Inicio" 
      />

      <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-12">
        {/* Stats Row - Responsive grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="stats-row">
          <StatCard
            label="Clientes activos"
            value={stats?.clientes_activos || 0}
            icon={Users}
          />
          <StatCard
            label="Servicios activos"
            value={stats?.servicios_activos || 0}
            note="Productos y Servicios"
            icon={Package}
          />
          <div onClick={fetchEgresosDetalle} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard
              label={`Egresos ${stats?.mes_actual || ''}`}
              value={`$${egresosTotal.total.toLocaleString()}`}
              note={`CXP: $${(egresosTotal.cxp || 0).toLocaleString()}`}
              icon={TrendDown}
            />
          </div>
          <div onClick={fetchIngresosDetalle} className="cursor-pointer hover:scale-[1.02] transition-transform">
            <StatCard
              label={`Ingresos ${stats?.mes_actual || ''}`}
              value={`$${(stats?.ingresos_mensuales || 0).toLocaleString()}`}
              note={`CXC: $${(stats?.ingresos_cxc || 0).toLocaleString()}`}
              icon={CurrencyDollar}
            />
          </div>
        </div>

        {/* Alerts - Grouped by month - Shows all 3 months data with scroll */}
        {alerts.length > 0 && (
          <div className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4 md:p-6" data-testid="alerts-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-['Fraunces'] text-lg md:text-xl font-bold">Alertas</h3>
              <span className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">
                {alerts.reduce((acc, m) => acc + m.alertas.length, 0)} alertas
              </span>
            </div>
            <div 
              className="space-y-4 md:space-y-6 overflow-y-auto pr-2 scrollbar-thin" 
              style={{ maxHeight: alertsHeight }}
              data-testid="alerts-scroll-container"
            >
              {alerts.map((monthGroup, idx) => (
                <div key={idx} className="space-y-3">
                  <h4 className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-xs md:text-sm uppercase tracking-wider border-b border-[#2a2e3d] pb-2 sticky top-0 bg-[#13161e] z-10">
                    {monthGroup.mes} {monthGroup.año} ({monthGroup.alertas.length})
                  </h4>
                  <div className="space-y-2 pl-1 md:pl-2">
                    {monthGroup.alertas.map((alert, alertIdx) => (
                      <AlertCard key={alertIdx} {...alert} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal Detalle Ingresos */}
      <Modal
        isOpen={showIngresosModal}
        onClose={() => setShowIngresosModal(false)}
        title={`Detalle Ingresos ${stats?.mes_actual || ''}`}
      >
        <div className="space-y-3">
          {ingresosDetalle.length === 0 ? (
            <p className="text-[#8b949e] text-center py-4">No hay ingresos para este mes</p>
          ) : (
            <>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {ingresosDetalle.map((producto, idx) => {
                  const precio = producto.precio_iva > 0 ? producto.precio_iva : producto.precio_base;
                  return (
                    <div key={idx} className="bg-[#0d0f14] border border-[#2a2e3d] rounded-sm p-3">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <span className="text-white font-['Mulish'] text-sm font-semibold">{producto.dominio_nombre || producto.nombre}</span>
                          <span className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-[10px] ml-2">{producto.tipo}</span>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] uppercase ${
                          producto.estado === 'vencido' 
                            ? 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]' 
                            : producto.estado === 'por vencer'
                            ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                            : 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]'
                        }`}>
                          {producto.estado}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#8b949e] font-['IBM_Plex_Mono']">Venc: {producto.fecha_vencimiento}</span>
                        <span className="text-[#00d68f] font-['IBM_Plex_Mono'] font-bold">${precio.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-[#2a2e3d] pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#8b949e] font-['IBM_Plex_Mono'] text-sm">Total ({ingresosDetalle.length} items)</span>
                  <span className="text-white font-['IBM_Plex_Mono'] text-lg font-bold">
                    ${ingresosDetalle.reduce((acc, p) => acc + (p.precio_iva > 0 ? p.precio_iva : p.precio_base), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Modal Detalle Egresos */}
      <Modal
        isOpen={showEgresosModal}
        onClose={() => setShowEgresosModal(false)}
        title={`Detalle Egresos ${stats?.mes_actual || ''}`}
      >
        <div className="space-y-3">
          {egresosDetalle.length === 0 ? (
            <p className="text-[#8b949e] text-center py-4">No hay egresos para este mes</p>
          ) : (
            <>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {egresosDetalle.map((item, idx) => (
                  <div key={idx} className="bg-[#0d0f14] border border-[#2a2e3d] rounded-sm p-3">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <span className="text-white font-['Mulish'] text-sm font-semibold">{item.nombre}</span>
                        <span className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-[10px] ml-2">{item.tipo}</span>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] uppercase ${
                        item.estado === 'vencido' 
                          ? 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]' 
                          : item.estado === 'por vencer'
                          ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                          : 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]'
                      }`}>
                        {item.estado}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8b949e] font-['IBM_Plex_Mono']">Venc: {item.fecha_vencimiento}</span>
                      <span className="text-[#ff5252] font-['IBM_Plex_Mono'] font-bold">${item.precio.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#2a2e3d] pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#8b949e] font-['IBM_Plex_Mono'] text-sm">Total ({egresosDetalle.length} items)</span>
                  <span className="text-white font-['IBM_Plex_Mono'] text-lg font-bold">
                    ${egresosDetalle.reduce((acc, item) => acc + item.precio, 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
